function isPlainObject(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * All keys optional
 * Recursive for nested objects
 */
export type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends (infer U)[]
        ? DeepPartial<U>[]
        : T[K] extends object | null
        ? DeepPartial<NonNullable<T[K]>> | null
        : T[K];
};

export function deepMerge<T>(base: T, patch: DeepPartial<T>): T {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const out: any = Array.isArray(base) ? [...base] : { ...base };

    for (const key of Object.keys(patch) as (keyof T)[]) {
        const provided = patch[key];

        if (provided === undefined) continue;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bv = (base as any)[key];

        if (Array.isArray(provided)) {
            out[key] = provided;
        } else if (isPlainObject(provided) && isPlainObject(bv)) {
            out[key] = deepMerge(bv, provided);
        } else {
            out[key] = provided;
        }
    }
    return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mockHandler: ProxyHandler<any> = {
    get(target, prop) {
        if (prop === "__typename") return "Story";
        const v = target[prop as keyof typeof target];

        // if explicitly provided (even null), use it
        if (v !== undefined) return v;

        // not provided -> act like everything defaults to null
        return null;
    },
};

// Wrap nested objects too so deep reads also return null by default
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepWrap = (node: any): any =>
    node && typeof node === "object" && !Array.isArray(node)
        ? new Proxy(
              Object.fromEntries(
                  Object.entries(node).map(([k, val]) => [k, deepWrap(val)])
              ),
              mockHandler
          )
        : node;
